// src/components/resources/DynamicResourceComponent.tsx

'use client'

import { isResourceInstance } from '~/lib/resources/types/shared/type-guards'
import type { ResourceInstance } from '~/lib/resources/types/shared/base'
import type { ToolType } from '~/lib/resources/types/shared/enums'
import { Suspense, type ComponentType } from 'react'
import dynamic from 'next/dynamic'

// Dynamic imports with proper error boundaries
const CreativeQualityGrader = dynamic(
  () => import('./calculators/CreativeQualityGrader').then(mod => mod.CreativeQualityGrader),
  {
    ssr: false,
    loading: () => <AnalyzerLoadingFallback title="Creative Quality Grader" />
  }
)

const ThruPlayRateBenchmark = dynamic(
  () => import('./calculators/ThruPlayRateBenchmark').then(mod => mod.ThruPlayRateBenchmark),
  {
    ssr: false,
    loading: () => <AnalyzerLoadingFallback title="ThruPlay Rate Benchmark" />
  }
)

const HookRateBenchmark = dynamic(
  () => import('./calculators/HookRateBenchmark').then(mod => mod.HookRateBenchmark),
  {
    ssr: false,
    loading: () => <AnalyzerLoadingFallback title="Hook Rate Benchmark" />
  }
)

// Interactive island only — the cited CPM/CPC/CTR tables render server-side
// via PaidSocialBenchmarkTables in the analyzer page, so first-paint HTML does
// not depend on this client bundle.
const PaidSocialBenchmarks = dynamic(
  () => import('./calculators/PaidSocialBenchmarks').then(mod => mod.PaidSocialBenchmarks),
  {
    ssr: false,
    loading: () => <AnalyzerLoadingFallback title="Paid Social Benchmarks" />
  }
)

interface DynamicResourceComponentProps {
  resource: ResourceInstance
}

/**
 * Fallback component for invalid or unavailable resources
 */
const ResourceNotAvailableFallback = (): JSX.Element => (
  <div className="p-6 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
    <div className="text-center">
      <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg mx-auto mb-4 flex items-center justify-center">
        <span className="text-2xl" role="img" aria-label="Warning">⚠️</span>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        Resource Not Available
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        This resource is currently unavailable. Please try again later.
      </p>
    </div>
  </div>
)

/**
 * Fallback component for unsupported resource types
 */
const UnsupportedResourceTypeFallback = ({ resource }: { resource: ResourceInstance }): JSX.Element => (
  <div className="p-6 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
    <div className="text-center">
      <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg mx-auto mb-4 flex items-center justify-center">
        <span className="text-2xl" role="img" aria-label="Document">📄</span>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        {resource.title || 'Resource'}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        This resource type is not yet supported in the interactive format.
      </p>
    </div>
  </div>
)

/**
 * Loading fallback for analyzer components
 */
const AnalyzerLoadingFallback = ({ title }: { title: string }): JSX.Element => (
  <div className="p-6 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
    <div className="text-center">
      <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg mx-auto mb-4 flex items-center justify-center">
        <span className="text-2xl" role="img" aria-label="Chart">📊</span>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Loading analyzer...
      </p>
      <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
    </div>
  </div>
)

/**
 * Generic fallback for unsupported tool types
 */
const UnsupportedToolTypeFallback = ({ 
  resource, 
  emoji, 
  message 
}: { 
  resource: ResourceInstance
  emoji: string
  message: string
}): JSX.Element => (
  <div className="p-6 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
    <div className="text-center">
      <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg mx-auto mb-4 flex items-center justify-center">
        <span className="text-2xl" role="img" aria-label="Tool">{emoji}</span>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        {resource.title || 'Tool'}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {message}
      </p>
    </div>
  </div>
)

/**
 * Renders analyzer components based on resource slug
 */
/**
 * Slug → bespoke analyzer component. Register a new analyzer by adding its
 * `dynamic()` import above and one entry here — no per-slug `if` branch.
 */
const ANALYZER_COMPONENTS: Record<string, ComponentType> = {
  'creative-quality-grader': CreativeQualityGrader,
  'hook-rate-benchmark': HookRateBenchmark,
  'paid-social-benchmarks': PaidSocialBenchmarks,
  'thruplay-rate-benchmark': ThruPlayRateBenchmark,
}

const renderAnalyzer = (resource: ResourceInstance): JSX.Element => {
  const Component = ANALYZER_COMPONENTS[resource.slug]
  if (Component) {
    return (
      <Suspense fallback={<AnalyzerLoadingFallback title={resource.title || 'Analyzer'} />}>
        <Component />
      </Suspense>
    )
  }

  return (
    <UnsupportedToolTypeFallback
      resource={resource}
      emoji="📊"
      message="This analyzer is not yet available."
    />
  )
}

/**
 * Maps tool types to their respective render functions.
 *
 * Only `analyzer` is wired: the sole caller is the analyzer route
 * (`app/resources/tools/analyzers/[slug]/page.tsx`), which always passes a
 * `ToolType.ANALYZER` resource. The former `calculator` (a `CalculatorLayout`
 * stub — real calculators render via their own route), `quiz`, and `generator`
 * ("coming soon" placeholders) entries were unreachable dead branches and were
 * removed. `Partial` because the map is no longer total over `ToolType`; any
 * non-analyzer toolType falls through to the defensive default in the component.
 */
const toolTypeRenderers: Partial<Record<ToolType, (resource: ResourceInstance) => JSX.Element>> = {
  analyzer: renderAnalyzer,
}

/**
 * Dynamic resource component that renders different tool types based on resource configuration.
 * Provides semantic fallbacks for SEO and accessibility.
 * 
 * @param props - Component properties
 * @param props.resource - The resource instance to render
 * @returns JSX element or semantic fallback
 */
export default function DynamicResourceComponent({ resource }: DynamicResourceComponentProps): JSX.Element {
  // Validate resource instance
  if (!isResourceInstance(resource)) {
    return <ResourceNotAvailableFallback />
  }

  // Ensure we're dealing with a tool type resource
  if (resource.resourceType !== 'tool') {
    return <UnsupportedResourceTypeFallback resource={resource} />
  }

  // Get the appropriate renderer for the tool type
  const renderer = toolTypeRenderers[resource.toolType]
  
  if (renderer) {
    return renderer(resource)
  }

  // Fallback for unknown tool types
  return (
    <UnsupportedToolTypeFallback
      resource={resource}
      emoji="🔧"
      message="This tool type is not yet supported."
    />
  )
}